import React, { FC, memo, MouseEvent, useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import classNames from 'classnames';
import { compose } from '@reduxjs/toolkit';

import type { BettingContextProps } from '../../types';
import type { RecordOfBetEntity } from 'app/lib/betting/betting-entities/reducers/stakes/types';
import type { ReduxState } from 'app/redux/types';

import { ContextNamespace } from 'app/constants/Context';
import { withinBettingContext } from 'app/contexts/betting';
import { BetPrice } from 'app/design-system/components/BetPrice';
import { ButtonType } from 'app/design-system/types/Button';
import usePrevious from 'app/lib/hooks/usePrevious';
import betSlipContext from 'app/modules/BetSlip/contexts/betSlip';
import { Names } from 'app/packages/lib/antlr/formatters/NamesFormatter';

import { getDataGaEventByNamespace } from '../../utils/data-ga-event-by-namespace';
import { useIsMatchComponent } from '../MatchDetails/hooks';

import styles from './Bet.module.scss';

type BetProps = {
  bet: RecordOfBetEntity;
  onClick: (netId: number) => void;
  isInBetSlip: boolean;
  conflict?: boolean;
  disabled: boolean;
  className?: string;
  priceClassName?: string;
  label?: string;
  labelType?: keyof Names;
};

type StoreProps = {
  namespace: ContextNamespace;
};

const Bet: FC<BetProps & StoreProps> = memo(({
  bet,
  onClick,
  isInBetSlip,
  conflict,
  disabled,
  className,
  priceClassName,
  label,
  labelType,
  namespace,
}) => {
  const dispatch = useDispatch();
  const [extraClass, setExtraClass] = useState('');
  const previousBetPrice = usePrevious(bet?.price || 0);
  const timeout = useRef<number | null>(null);
  const isMatchComponent = useIsMatchComponent();
  const [dataGaEvent, setDataGaEvent] = useState('');
  const isDisabled = disabled && !conflict;

  const dataGaAttributes = {
    ['data-ga-event']: dataGaEvent,
    ['data-ga-event-content']: bet.id,
    ['data-ga-event-value']: bet.price,
    ['data-ga-sport-match']: bet.matchId,
  };

  const handleClick = (event: MouseEvent) => {
    if (conflict) {
      dispatch(betSlipContext.actions.conflicts(Date.now()));
    }
    else {
      event.preventDefault();
      !disabled && onClick(bet.id);
    }
  };

  useEffect(() => {
    let gaEvent = '';

    if (isMatchComponent) {
      gaEvent = isInBetSlip
        ? 'details-removeFromCart'
        : 'details-addToCart';
    }
    else {
      gaEvent = isInBetSlip
        ? getDataGaEventByNamespace(namespace, 'removeFromCart')
        : getDataGaEventByNamespace(namespace, 'addToCart');
    }

    setDataGaEvent(gaEvent);
  }, [isInBetSlip]);

  /* Update bet `background` color if `price` was changed */
  useEffect(() => {
    let betBackgroundClassname = '';
    const isMatchOfDay = namespace === ContextNamespace.MATCH_OF_THE_DAY;

    if (bet.price && previousBetPrice !== bet.price && !isInBetSlip) {
      if ((bet.price - previousBetPrice) > 0) {
        betBackgroundClassname = isMatchOfDay
          ? styles.increasedMatchOfDay
          : styles.increased;
      }
      else {
        betBackgroundClassname = isMatchOfDay
          ? styles.decreasedMatchOfDay
          : styles.decreased;
      }
    }

    if (betBackgroundClassname) {
      setExtraClass(betBackgroundClassname);

      timeout.current = window.setTimeout(() => {
        setExtraClass('');
      }, 3000);
    }

    return () => {
      timeout.current && clearTimeout(timeout.current);
    };
  }, [bet.price, isInBetSlip]);

  useEffect(() => {
    if (extraClass && isInBetSlip && timeout.current) {
      setExtraClass('');
      clearTimeout(timeout.current);
    }
  }, [isInBetSlip]);

  return (
    <button
      // title={bet.names?.get('full')}
      title={bet.names?.get('long')}
      type={ButtonType.BUTTON}
      className={
        classNames(
          styles.bet,
          className,
          { active: isInBetSlip, disabled: isDisabled, [extraClass]: extraClass, conflict },
        )}
      {...dataGaAttributes}
      disabled={isDisabled}
      onClick={handleClick}
      data-test-selected={isInBetSlip}
      data-test={`bet-${bet.key}-${bet.id}`}>
      {Boolean(label) && (
        <span className={styles.betLabel}
              data-test={`bet-${bet.id}-label`}>
          {label}
        </span>
      )}

      {labelType && (
        <span className={styles.betLabel}
              data-test={`bet-${bet.id}-label`}>
          {bet.names.get(labelType) || '—'}
        </span>
      )}

      <BetPrice className={priceClassName}
                value={bet.price}
                betId={bet.id} />
    </button>
  );
});

const mapStateToProps = (_: ReduxState, {
  $betting: {
    namespace,
  },
}: BettingContextProps): StoreProps => {
  return { namespace };
};

export default compose<FC<BetProps>>(
  withinBettingContext,
  connect(mapStateToProps),
)(Bet);
